import React, { Suspense } from 'react';
import { usePathname } from 'expo-router';
import { useExploreLightboxStore } from '@/store';

const LazyExploreLightbox = React.lazy(() =>
  import('@/components/Explore/ExploreLightbox')
);

/**
 * Thin lazy-mount shell for ExploreLightbox (~3.3k lines + expo-video/audio).
 *
 * Duplicate mount strategy (intentional):
 * - `(public)/_layout` — landing Explore showcase calls `openExploreLightbox`;
 *   must sit as a Stack sibling so the overlay wins the nav header z-index.
 * - `(app)/_layout` — `/explore` and deep-linked explore items.
 *
 * Both shells mount this host, but neither statically imports ExploreLightbox.jsx.
 * Metro emits one shared async chunk; it loads only when `isOpen` becomes true.
 *
 * @param {object} props
 * @param {boolean} [props.skipOnDocsLearn] — skip mounting on `/docs/*` and `/learn/*` (3.1.w).
 */
export default function ExploreLightboxHost({ skipOnDocsLearn = false }) {
  const isOpen = useExploreLightboxStore((state) => state.isOpen);
  const pathname = usePathname();

  if (skipOnDocsLearn && (pathname?.startsWith('/docs') || pathname?.startsWith('/learn'))) {
    return null;
  }

  if (!isOpen) return null;

  return (
    <Suspense fallback={null}>
      <LazyExploreLightbox />
    </Suspense>
  );
}
